import { CAMPAIGN_STATUS_BADGE_PROPS } from 'entities/campaign';
import type {
  CrmHistoryEventTypes,
  CrmHistoryModel,
  LeadOrAppointmentHistoryEventTypes,
  LeadOrAppointmentHistoryModel,
  CampaignHistoryModel,
  ChangeViews,
  GroupHistoryModel,
  InvoiceHistoryModel,
  InvoiceHistoryEventTypes,
} from 'entities/history';
import type { BadgeProps } from 'shared/ui/Badge';

export function mapLeadOrAppointmentEventToChangeView({ eventType, body }: LeadOrAppointmentHistoryModel): ChangeViews {
  switch (eventType) {
    case 'lead_assigned': {
      return { viewType: 'AssignedEvent', data: body };
    }
    case 'lead_billable': {
      return {
        viewType: 'OldToNewValueOnOff',
        oldValue: body.old,
        newValue: body.new,
        reason: body.reason,
        reasonId: body.reasonId,
        reasonDescription: body.reasonDescription,
      };
    }
    default:
      throw new Error(`Unknown campaign event type: ${eventType}`);
  }
}

export const LEAD_HISTORY_EVENTS_BADGE_PROPS: Record<LeadOrAppointmentHistoryEventTypes, BadgeProps> = {
  lead_assigned: { text: 'Lead assigned', color: 'deepGreen', size: 'lg' },
  lead_billable: { text: 'Lead billable', color: 'deepGreen', size: 'lg' },
};

export function mapInvoiceEventToChangeView({ eventType, body }: InvoiceHistoryModel): ChangeViews {
  switch (eventType) {
    case 'invoice_created':
    case 'invoice_charge_successful':
    case 'invoice_charge_failed': {
      return { viewType: 'SeveralRowsValueOverflow', data: body };
    }
    case 'invoice_status_updated':
      return { viewType: 'OldToNewValueTextCapitalized', oldValue: body.old, newValue: body.new };
    case 'invoice_updated':
    case 'invoice_regenerated': {
      return { viewType: 'InvoiceMultipleRowOldNewValues', data: body };
    }
    default:
      throw new Error(`Unknown campaign event type: ${eventType}`);
  }
}

export const INVOICE_HISTORY_EVENTS_BADGE_PROPS: Record<InvoiceHistoryEventTypes, BadgeProps> = {
  invoice_charge_failed: { text: 'Charge failed', color: 'red', size: 'mdp' },
  invoice_created: { text: 'Invoice created', color: 'slate', size: 'mdp' },
  invoice_status_updated: { text: 'Invoice status updated', color: 'deepBlue', size: 'mdp' },
  invoice_regenerated: { text: 'Invoice re-generated', color: 'yellow', size: 'mdp' },
  invoice_updated: { text: 'Invoice updated', color: 'mint', size: 'mdp' },
  invoice_charge_successful: { text: 'Charge successful', color: 'deepGreen', size: 'mdp' },
};

export const CRM_HISTORY_EVENTS_BADGE_PROPS: Record<CrmHistoryEventTypes, BadgeProps> = {
  crm_request_sent_to_queue: { text: 'Request sent to queue', color: 'deepGreen', size: 'extraLg' },
  crm_response_positive: { text: 'Positive response from CRM', color: 'deepGreen', size: 'extraLg' },
  crm_response_failed: { text: 'Failed request to CRM', color: 'red', size: 'extraLg' },
  crm_response_negative: { text: 'Negative response from CRM', color: 'red', size: 'extraLg' },
};

export function mapCrmEventToChangeView(row: CrmHistoryModel): ChangeViews {
  return { viewType: 'SeveralRowsValue', data: row.body };
}

export function mapCampaignEventToChangeView({ eventType, body }: CampaignHistoryModel): ChangeViews {
  switch (eventType) {
    case 'campaign_created': {
      return { viewType: 'SeveralRowsValue', data: body };
    }
    case 'campaign_name_updated':
    case 'campaign_calendar_url_updated': {
      return { viewType: 'OldToNewValueText', oldValue: body.old, newValue: body.new };
    }
    case 'campaign_pausing_reason_updated': {
      return { viewType: 'OldToNewValuePausingReasons', oldValue: body.old, newValue: body.new };
    }
    case 'campaign_status_updated': {
      return {
        viewType: 'OldToNewValueBadge',
        oldValue: CAMPAIGN_STATUS_BADGE_PROPS[body.old],
        newValue: CAMPAIGN_STATUS_BADGE_PROPS[body.new],
      };
    }
    case 'campaign_is_active_updated': {
      return { viewType: 'OldToNewValueIsActive', oldValue: body.old, newValue: body.new };
    }
    case 'campaign_target_area_updated':
      return { viewType: 'OldToNewValueNumber', oldValue: body.old, newValue: body.new };
    case 'campaign_minimal_income_updated':
      return {
        viewType: 'OldToNewValueMoney',
        oldValue: body.old,
        newValue: body.new,
      };
    case 'campaign_budget_updated': {
      return {
        viewType: 'OldToNewValueMoneyDate',
        oldValue: body.budget.old,
        newValue: body.budget.new,
        effectiveAt: body.effective_at,
      };
    }
    case 'campaign_bid_updated': {
      return { viewType: 'OldToNewValueBidOrBidTiers', oldValue: body.old, newValue: body.new };
    }
    case 'campaign_schedule_updated': {
      return { viewType: 'SchedulingUpdateCell', data: body };
    }
    case 'integration_created': {
      return { viewType: 'IntegrationEventCreated', isActive: body.is_active };
    }
    case 'integration_updated': {
      return { viewType: 'IntegrationEventUpdated', isActive: body.is_active.new };
    }
    case 'integration_deleted': {
      return { viewType: 'IntegrationEventDeleted', label: body.label };
    }
    case 'test_crm_request': {
      return { viewType: 'SeveralRowsValue', data: body };
    }
    default:
      throw new Error(`Unknown campaign event type: ${eventType}`);
  }
}

export function mapGroupEventToChangeView({ eventType, body }: GroupHistoryModel): ChangeViews {
  switch (eventType) {
    case 'group_created': {
      return { viewType: 'SeveralRowsValue', data: body };
    }
    case 'group_campaigns_updated': {
      return { viewType: 'OldToNewValueText', oldValue: body.old, newValue: body.new };
    }
    case 'group_excluded_zips_updated': {
      return { viewType: 'OldToNewValueNumber', oldValue: body.old, newValue: body.new };
    }
    case 'group_google_network_updated': {
      return { viewType: 'OldToNewValueIsApplied', oldValue: body.old, newValue: body.new };
    }
    case 'group_name_updated': {
      return { viewType: 'OldToNewValueText', oldValue: body.old, newValue: body.new };
    }
    case 'group_traffic_days_updated': {
      return { viewType: 'OldToNewValueTrafficDays', oldValue: body.old, newValue: body.new };
    }
    case 'group_traffic_time_updated': {
      return { viewType: 'OldToNewValueText', oldValue: body.old, newValue: body.new };
    }
    case 'filters_applied': {
      return { viewType: 'OldToNewSeveralRows', data: body };
    }
    default:
      throw new Error(`Unknown group event type: ${eventType}`);
  }
}
